import React, { useEffect } from 'react'
import AdminDashboardLayout from './AdminDashboardLayout'
import { useDispatch, useSelector } from 'react-redux';
import { getSuperAdminStatsAPI } from '../../redux/slices/dashboardStatsSlice';
import {
    AreaChart,
    Area,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    ResponsiveContainer,
    PieChart,
    Pie,
    Cell,
    BarChart,
    Bar,
    Legend
} from 'recharts';
import './SuperAdminDashboard.css';

const SuperAdminDashboard = () => {

    const dispatch = useDispatch();
    const loading = useSelector((state) => state?.dashboardStatsSlice?.loading);
    const superAdminStats = useSelector((state) => state?.dashboardStatsSlice?.superAdminStats);

    const fetchStats = async () => {
        try {
            await dispatch(getSuperAdminStatsAPI());
        } catch (error) {
            console.error(error);
        }
    };

    useEffect(() => {
        fetchStats();
    }, []);

    const statsCards = [
        {
            title: 'Total Companies',
            value: superAdminStats?.total_corporates || 0,
            icon: 'fa-solid fa-building',
            color: '#EF4444'
        },
        {
            title: 'Total Employees',
            value: superAdminStats?.total_employees || 0,
            icon: 'fa-solid fa-id-badge',
            color: '#60A5FA'
        },
        {
            title: 'Total Professionals',
            value: superAdminStats?.total_professionals || 0,
            icon: 'fa-solid fa-user-tie',
            color: '#4ADE80'
        },
        {
            title: 'Total Users',
            value: superAdminStats?.total_users || 0,
            icon: 'fa-solid fa-users',
            color: '#FBBF24'
        }
    ];

    // Red, Blue, Green color scheme for charts
    const COLORS = ['#EF4444', '#60A5FA', '#4ADE80', '#FBBF24'];
    const STATUS_COLORS = ['#EF4444', '#60A5FA', '#4ADE80'];

    const CustomTooltip = ({ active, payload, label }) => {
        if (active && payload && payload.length) {
            return (
                <div className="chart-custom-tooltip">
                    <p className="tooltip-label">{label}</p>
                    {payload.map((entry, index) => (
                        <p key={index} className="tooltip-value" style={{ color: entry.color }}>
                            {entry.name}: {entry.value}
                        </p>
                    ))}
                </div>
            );
        }
        return null;
    };

    const renderCustomizedLabel = ({ cx, cy, midAngle, innerRadius, outerRadius, percent, name }) => {
        const RADIAN = Math.PI / 180;
        const radius = innerRadius + (outerRadius - innerRadius) * 0.5;
        const x = cx + radius * Math.cos(-midAngle * RADIAN);
        const y = cy + radius * Math.sin(-midAngle * RADIAN);

        return percent > 0.05 ? (
            <text x={x} y={y} fill="white" textAnchor="middle" dominantBaseline="central" fontSize={12} fontWeight={600}>
                {`${(percent * 100).toFixed(0)}%`}
            </text>
        ) : null;
    };

    return (
        <AdminDashboardLayout>
            <div className="admin-dashboard">
                <h1 className="admin-dashboard-title">Dashboard</h1>

                {/* Stats Cards */}
                <div className="admin-stats-grid">
                    {statsCards.map((card, index) => (
                        <div key={index} className="admin-stat-card">
                            <div className="admin-stat-icon" style={{ backgroundColor: `${card.color}15`, color: card.color }}>
                                <i className={card.icon}></i>
                            </div>
                            <div className="admin-stat-content">
                                <p className="admin-stat-label">{card.title}</p>
                                {loading ? (
                                    <div className="admin-stat-loader"></div>
                                ) : (
                                    <h2 className="admin-stat-value">{card.value}</h2>
                                )}
                            </div>
                        </div>
                    ))}
                </div>

                {/* Charts Section */}
                <div className="admin-charts-grid">
                    {/* Monthly Registrations Chart */}
                    <div className="admin-chart-card chart-large">
                        <div className="admin-chart-header">
                            <h3 className="admin-chart-title">
                                <i className="fas fa-chart-line"></i>
                                Monthly Registrations
                            </h3>
                            <span className="admin-chart-subtitle">Last 6 months</span>
                        </div>
                        <div className="admin-chart-body">
                            {loading ? (
                                <div className="chart-loading">
                                    <div className="admin-stat-loader"></div>
                                </div>
                            ) : (
                                <ResponsiveContainer width="100%" height={300}>
                                    <AreaChart
                                        data={superAdminStats?.monthly_registrations || []}
                                        margin={{ top: 10, right: 30, left: 0, bottom: 0 }}
                                    >
                                        <defs>
                                            <linearGradient id="colorCorporates" x1="0" y1="0" x2="0" y2="1">
                                                <stop offset="5%" stopColor="#EF4444" stopOpacity={0.4} />
                                                <stop offset="95%" stopColor="#EF4444" stopOpacity={0} />
                                            </linearGradient>
                                            <linearGradient id="colorProfessionals" x1="0" y1="0" x2="0" y2="1">
                                                <stop offset="5%" stopColor="#60A5FA" stopOpacity={0.4} />
                                                <stop offset="95%" stopColor="#60A5FA" stopOpacity={0} />
                                            </linearGradient>
                                            <linearGradient id="colorUsers" x1="0" y1="0" x2="0" y2="1">
                                                <stop offset="5%" stopColor="#4ADE80" stopOpacity={0.4} />
                                                <stop offset="95%" stopColor="#4ADE80" stopOpacity={0} />
                                            </linearGradient>
                                        </defs>
                                        <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                                        <XAxis dataKey="month" stroke="#64748b" fontSize={12} />
                                        <YAxis stroke="#64748b" fontSize={12} />
                                        <Tooltip content={<CustomTooltip />} />
                                        <Legend />
                                        <Area
                                            type="monotone"
                                            dataKey="corporates"
                                            name="Corporates"
                                            stroke="#EF4444"
                                            strokeWidth={2}
                                            fillOpacity={1}
                                            fill="url(#colorCorporates)"
                                        />
                                        <Area
                                            type="monotone"
                                            dataKey="professionals"
                                            name="Professionals"
                                            stroke="#60A5FA"
                                            strokeWidth={2}
                                            fillOpacity={1}
                                            fill="url(#colorProfessionals)"
                                        />
                                        <Area
                                            type="monotone"
                                            dataKey="users"
                                            name="Users"
                                            stroke="#4ADE80"
                                            strokeWidth={2}
                                            fillOpacity={1}
                                            fill="url(#colorUsers)"
                                        />
                                    </AreaChart>
                                </ResponsiveContainer>
                            )}
                        </div>
                    </div>

                    {/* Status Distribution Chart */}
                    <div className="admin-chart-card">
                        <div className="admin-chart-header">
                            <h3 className="admin-chart-title">
                                <i className="fas fa-chart-pie"></i>
                                Status Distribution
                            </h3>
                            <span className="admin-chart-subtitle">All users</span>
                        </div>
                        <div className="admin-chart-body">
                            {loading ? (
                                <div className="chart-loading">
                                    <div className="admin-stat-loader"></div>
                                </div>
                            ) : (
                                <ResponsiveContainer width="100%" height={280}>
                                    <PieChart>
                                        <Pie
                                            data={superAdminStats?.status_distribution || []}
                                            cx="50%"
                                            cy="50%"
                                            labelLine={false}
                                            label={renderCustomizedLabel}
                                            outerRadius={100}
                                            innerRadius={50}
                                            fill="#8884d8"
                                            dataKey="value"
                                            paddingAngle={2}
                                        >
                                            {(superAdminStats?.status_distribution || []).map((entry, index) => (
                                                <Cell key={`cell-${index}`} fill={STATUS_COLORS[index % STATUS_COLORS.length]} />
                                            ))}
                                        </Pie>
                                        <Tooltip />
                                        <Legend />
                                    </PieChart>
                                </ResponsiveContainer>
                            )}
                        </div>
                    </div>

                    {/* User Type Distribution Chart */}
                    <div className="admin-chart-card">
                        <div className="admin-chart-header">
                            <h3 className="admin-chart-title">
                                <i className="fas fa-chart-bar"></i>
                                User Distribution
                            </h3>
                            <span className="admin-chart-subtitle">By type</span>
                        </div>
                        <div className="admin-chart-body">
                            {loading ? (
                                <div className="chart-loading">
                                    <div className="admin-stat-loader"></div>
                                </div>
                            ) : (
                                <ResponsiveContainer width="100%" height={280}>
                                    <BarChart
                                        data={superAdminStats?.user_type_distribution || []}
                                        margin={{ top: 20, right: 30, left: 0, bottom: 5 }}
                                    >
                                        <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                                        <XAxis dataKey="name" stroke="#64748b" fontSize={12} />
                                        <YAxis stroke="#64748b" fontSize={12} />
                                        <Tooltip content={<CustomTooltip />} />
                                        <Bar dataKey="value" name="Count" radius={[4, 4, 0, 0]}>
                                            {(superAdminStats?.user_type_distribution || []).map((entry, index) => (
                                                <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                                            ))}
                                        </Bar>
                                    </BarChart>
                                </ResponsiveContainer>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </AdminDashboardLayout>
    )
}

export default SuperAdminDashboard
